import { useCurrentAccount } from '@application/api';
import {
  DogBreederReviewListController,
  PreloadController,
  SeoController,
  setWhatsappBreederId,
  useBuyerChecklistModal,
  useSocialShareModal,
  useWhatsappModal,
} from '@application/components';
import { RedirectWithStatus } from '@application/components/app/App/RedirectWithStatus';
import type { PageState } from '@application/types';
import {
  emailShareUrl,
  facebookShareUrl,
  twitterShareUrl,
  useRouter,
  useTranslatedUrl,
  waShareUrl,
} from '@application/utils';
import { BreederPage } from '@presentation/pages';
import { PageSkeleton } from '@presentation/pages/Breeder/components/PageSkeleton';
import type { LocationDescriptor } from 'history';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslatableContentState } from '@application/state';
import { Analytics } from '@application/utils/analytics';
import { useLocation } from 'react-router-dom';
import { useFormatBreeders } from '../BreederListPageController/api/useFormatBreeders';
import { useFormatLitters } from '../LitterListPageController/api/useFormatLitters';
import { useFormatBreeder } from './api/useFormatBreeder';
import { useGetBreeder } from './api/useGetBreeder';
import { useGetBreeders } from './api/useGetBreeders';
import { useGetLitters } from './api/useGetLitters';
import { useRelatedLinks } from './api/useRelatedLinks';
import { useReviewList } from './api/useReviewList';
import { useSeo } from './useSeo';
import { useRedirect } from './useRedirect';

export const BreederPageController = () => {
  const {
    infoFromCurrentPath,
    translatedRoute,
    routeLocationDescriptor,
    getLocaleLinksForTranslations,
    language,
  } = useRouter();
  const { pathname } = useLocation();
  const { show: showShareModal, setUrls } = useSocialShareModal();
  const { modalHook: whatsappModal } = useWhatsappModal();
  const id = infoFromCurrentPath.params[0];
  const { currentAccountLoggedIn } = useCurrentAccount();

  const { breeder, breederLoading, breederFilteredBreeds, breederError } = useGetBreeder(id);
  const { setOriginalLang } = useTranslatableContentState();
  const { formattedBreeder, imagesToPreload, contentOriginalLanguage } = useFormatBreeder(
    breeder,
    breederFilteredBreeds,
  );

  useEffect(() => {
    if (contentOriginalLanguage) {
      setOriginalLang(contentOriginalLanguage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentOriginalLanguage]);

  const { currentAccountType } = useCurrentAccount();
  const dogBreedIds = (breeder?.breederBreeds ?? []).map((x) => x.dogBreed.id);

  // Unique base breed names
  const baseDogBreedNames = useMemo(() => {
    return Array.from(
      new Set(
        (breeder?.breederBreeds ?? [])
          .filter((x) => !x.dogBreed.isSubBreed)
          .map((x) => x.dogBreed.baseBreedName ?? x.dogBreed.name)
          .filter((x) => !!x) as string[],
      ),
    );
  }, [breeder?.breederBreeds]);

  const { reviewList: reviewSummary } = useReviewList(breeder?.id);

  const { relatedLinks } = useRelatedLinks(dogBreedIds);
  useTranslatedUrl(breeder?.translations);

  const { litters, littersLoading } = useGetLitters(breeder?.id, dogBreedIds);
  const { breeders, breedersLoading } = useGetBreeders(breeder?.id, dogBreedIds);
  const { formattedLitters } = useFormatLitters(litters);
  const { formattedBreeders } = useFormatBreeders(breeders);

  const breederUrl =
    breeder?.translations.find((x) => x.language === language)?.url ??
    breeder?.translations[0]?.url;
  useRedirect({ currentAccountLoggedIn, breederUrl });

  const { show: showBuyerChecklistModal } = useBuyerChecklistModal();

  const { title, description, keywords, canonicalUrl, breederName } = useSeo(
    breeder,
    breederFilteredBreeds,
  );

  const pageState = useMemo((): PageState => {
    if (breederLoading) return 'loading';
    if (breederError) return 'error';
    if (!breeder) return 'empty';
    return 'data';
  }, [breeder, breederError, breederLoading]);

  const backLink = useMemo((): LocationDescriptor => {
    return routeLocationDescriptor('breeder', {
      search: infoFromCurrentPath?.state?.returnSearch ?? {},
      params: infoFromCurrentPath?.state?.returnBreed ?? [],
    });
  }, [
    infoFromCurrentPath?.state?.returnBreed,
    infoFromCurrentPath?.state?.returnSearch,
    routeLocationDescriptor,
  ]);

  const moreLittersLink = routeLocationDescriptor('litterList', {
    ...(baseDogBreedNames?.length > 1
      ? { search: { breeds: baseDogBreedNames } }
      : { params: baseDogBreedNames }),
  });
  const moreBreedersLink = routeLocationDescriptor('breederList', {
    ...(baseDogBreedNames?.length > 1
      ? { search: { breeds: baseDogBreedNames } }
      : { params: baseDogBreedNames }),
  });

  const socialShareClickHandler = useCallback(() => {
    const url = `https://tipaw.com${pathname}`;

    showShareModal();

    setUrls({
      facebook: facebookShareUrl(url),
      twitter: twitterShareUrl(url, title),
      whatsapp: waShareUrl(url),
      email: emailShareUrl(url, title, `${title}\n\n${description}`),
    });
  }, [description, pathname, setUrls, showShareModal, title]);

  const enableWhatsAppContact = useMemo(() => {
    return !!breeder?.whatsappNumber;
  }, [breeder?.whatsappNumber]);

  const onWhatsappClickHandler = useCallback(() => {
    Analytics.clickOnBreederWhatsapp();
    whatsappModal.show();
  }, [whatsappModal]);

  useEffect(() => {
    if (enableWhatsAppContact && breeder?.id) {
      setWhatsappBreederId(breeder.id);
    }
  }, [breeder?.id, enableWhatsAppContact]);

  const showApplyButton = useMemo(() => {
    return !enableWhatsAppContact && currentAccountType !== 'breeder';
  }, [currentAccountType, enableWhatsAppContact]);

  // Server redirect when breeder account is deleted
  if (breeder && (!breeder.certified || !breeder?.public)) {
    return <RedirectWithStatus to={translatedRoute('breederList')} status={301} />;
  }

  return (
    <>
      <PreloadController images={imagesToPreload} />
      <SeoController
        title={title}
        description={description}
        keywords={keywords}
        thumbnail={formattedBreeder?.slideshow?.items?.find((item) => !item.isVideo)?.src}
        localeLinks={getLocaleLinksForTranslations(breeder?.translations)}
        canonical={canonicalUrl}
        // We want to speed up the canonica... process on search engines,
        // but this won't make a diff then using canonical
        noIndex={!!canonicalUrl}
      />
      {pageState === 'loading' && <PageSkeleton backLink={backLink} />}
      {(pageState === 'empty' || pageState === 'error') && (
        <RedirectWithStatus status={404} to="/404" />
      )}
      {pageState === 'data' && breeder?.id && (
        <BreederPage
          {...formattedBreeder}
          breederName={breederName ?? ''}
          breedsLink={routeLocationDescriptor('breedList')}
          litterItems={formattedLitters}
          breederItems={formattedBreeders}
          litterItemsLoading={littersLoading}
          breederItemsLoading={breedersLoading}
          reviewSummary={reviewSummary}
          reviewListComponent={<DogBreederReviewListController dogBreederId={breeder.id} />}
          relatedLinks={relatedLinks}
          showApplyButton={showApplyButton}
          country={breeder.account.address?.country}
          moreLittersLink={moreLittersLink}
          moreBreedersLink={moreBreedersLink}
          onOpenBuyerChecklist={showBuyerChecklistModal}
          onSocialShareClick={socialShareClickHandler}
          enableWhatsAppContact={enableWhatsAppContact}
          onWhatsappClick={onWhatsappClickHandler}
        />
      )}
    </>
  );
};
